import { useVirtualizer } from '@tanstack/react-virtual';
import { useCallback, useMemo, useState, type FC } from 'react';

import { useTranslation } from '@mint-lib/i18n';
import {
  ComboboxInput,
  ComboboxInputHelperText,
  ComboboxInputLabel,
  ComboboxInputRoot,
  ComboboxItem,
  ComboboxMenu,
} from '@mint-lib/tw-components/patterns';

import type { OptionItem } from '../types/shared.js';

type Props = {
  id: string;
  label?: string;
  a11yLabel: string;
  options: OptionItem[];
  value: string;
  onChange: (option: OptionItem) => void;
  disabled?: boolean;
  helperText?: string;
  error?: boolean;
};

const ROW_HEIGHT_PX = 40;
const OVERSCAN_COUNT = 5;

const SelectWithSearch: FC<Props> = ({
  id,
  label,
  a11yLabel,
  options,
  value,
  onChange,
  disabled = false,
  helperText,
  error = false,
}) => {
  const { t } = useTranslation('@myn/approval_v2');
  const [isOpen, setIsOpen] = useState(false);
  const [scrollElement, setScrollElement] = useState<HTMLDivElement | null>(
    null,
  );
  const [searchValue, setSearchValue] = useState('');

  const filteredOptions = useMemo(() => {
    if (!searchValue) {
      return options;
    }

    return options.filter((option) =>
      option.label.toLowerCase().includes(searchValue.toLowerCase()),
    );
  }, [options, searchValue]);

  const listRef = useCallback((node: HTMLDivElement | null) => {
    const parent: HTMLDivElement | null =
      node?.closest('[data-slot="command-list"]') ?? null;
    setScrollElement(parent);
  }, []);

  const rowVirtualizer = useVirtualizer({
    count: filteredOptions.length,
    getScrollElement: () => scrollElement,
    estimateSize: () => ROW_HEIGHT_PX,
    overscan: OVERSCAN_COUNT,
  });

  const mapLabelByValue = useMemo(() => {
    return options.reduce<Record<string, string>>((acc, item) => {
      acc[item.value] = item.label;
      return acc;
    }, {});
  }, [options]);

  const handleSelect = (selectedValue: string) => {
    onChange({ value: selectedValue, label: mapLabelByValue[selectedValue] });
    setIsOpen(false);
    setSearchValue('');
  };

  const handleOpenChange = (open: boolean) => {
    setIsOpen(open);

    if (open) {
      requestAnimationFrame(() => {
        scrollElement?.scrollTo({ top: 0 });
        rowVirtualizer.scrollToOffset(0);
      });
    }
  };

  return (
    <ComboboxInputRoot className="w-full">
      {label ? (
        <ComboboxInputLabel htmlFor={id} label={label} disabled={disabled} />
      ) : null}
      <ComboboxInput
        aria-label={label}
        data-dd-action-name={a11yLabel}
        value={mapLabelByValue[value]}
        placeholder={t('Select')}
        open={isOpen}
        onOpenChange={handleOpenChange}
        disabled={disabled}
        inputProps={{ id, autoComplete: 'off' }}
        isPortal
        color={error ? 'error' : 'default'}
      >
        <ComboboxMenu
          withSearch
          emptyMessage={t('No options found')}
          shouldFilter={false}
          onValueChange={setSearchValue}
        >
          <div
            ref={listRef}
            className="relative w-full"
            style={{
              height: `${rowVirtualizer.getTotalSize()}px`,
            }}
          >
            {rowVirtualizer.getVirtualItems().map((virtualItem, index) => {
              const option = filteredOptions[virtualItem.index];
              return (
                <ComboboxItem
                  key={option.value}
                  data-dd-action-name={`Select option ${index + 1}`}
                  className="absolute top-0 left-0 w-full"
                  value={option.value}
                  onSelect={handleSelect}
                  data-state={value === option.value ? 'checked' : 'unchecked'}
                  keywords={[option.label]}
                  style={{
                    height: `${virtualItem.size}px`,
                    transform: `translateY(${virtualItem.start}px)`,
                  }}
                >
                  {option.label}
                </ComboboxItem>
              );
            })}
          </div>
        </ComboboxMenu>
      </ComboboxInput>
      {helperText ? (
        <ComboboxInputHelperText
          color={error ? 'error' : 'default'}
          helperText={helperText}
          disabled={disabled}
        />
      ) : null}
    </ComboboxInputRoot>
  );
};

export default SelectWithSearch;
